import { Button, Group, Paper, Text } from "@mantine/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCurrentUser } from "../hooks/useAuth.js";
import { useEndViewAs } from "../hooks/useViewAs.js";

/** Muss zu ASSUMED_VIEW_TTL_S in apps/api/src/session.ts passen (30 min). */
const ASSUMED_VIEW_TTL_MINUTES = 30;

/**
 * Dauerhaftes Band über dem Inhalt, solange eine fremde Sicht übernommen ist.
 *
 * Es ist absichtlich nicht wegklickbar und absichtlich laut: Wer vergisst, in
 * wessen Namen er gerade schreibt, richtet Schaden in fremden Daten an. Die
 * Restzeit steht mit dabei, weil die Übernahme nach dreißig Minuten von selbst
 * endet — ohne Anzeige wäre der Rückfall ein unerklärlicher Sprung.
 */
export function ViewAsBanner() {
  const { user } = useCurrentUser();
  const beenden = useEndViewAs();
  const { t } = useTranslation();
  const [jetzt, setJetzt] = useState(() => Date.now());

  const assumed = user?.assumedBy;

  useEffect(() => {
    if (!assumed) return;
    const id = setInterval(() => setJetzt(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [assumed]);

  if (!assumed) return null;

  // Nach oben auf die Laufzeit begrenzt: `expiresAt` kommt vom Server, `jetzt`
  // vom Browser. Geht die Uhr des Rechners auch nur eine Sekunde nach, ergäbe
  // das Aufrunden „Noch 31 Min." für eine Übernahme, die dreißig Minuten gilt —
  // eine Zahl, die es nie geben darf.
  const restSekunden = Math.max(0, assumed.expiresAt - Math.floor(jetzt / 1000));
  const restMinuten = Math.min(
    ASSUMED_VIEW_TTL_MINUTES,
    Math.ceil(restSekunden / 60),
  );

  return (
    <Paper
      p="xs"
      radius={0}
      withBorder
      bg="var(--msc-flaeche-gelb-warn)"
      style={{ borderLeft: "4px solid var(--mantine-color-yellow-7)" }}
    >
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm">
          {t("viewAs.bannerActing", {
            target: user!.fullName,
            real: assumed.byFullName,
          })}{" "}
          <Text span size="sm" c="dimmed">
            {t("viewAs.bannerRemaining", { minutes: restMinuten })}
          </Text>
        </Text>
        <Button
          size="compact-sm"
          variant="filled"
          color="yellow.8"
          onClick={() => beenden.mutate()}
          loading={beenden.isPending}
        >
          {t("viewAs.end")}
        </Button>
      </Group>
    </Paper>
  );
}
